/**
 * System Status — pure wire types + display model (Flow L; ADR-035 / S6).
 *
 * No React. The trust-boundary types (re-exported from the generated
 * ``api-types.gen``), the hand-authored SSE-frame types + runtime guard, the
 * rollup-state vocabulary + its display metadata (REQ-UI-046 status
 * palette), and the snapshot⊕stream merge that resolves a single stoplight
 * per component. Kept React-free so it's unit-testable without the DOM and so
 * the panel/diagram stay pure component exports
 * (react-refresh/only-export-components).
 *
 * # The two feeds
 *
 * Per the storyboard §6 (snapshot-then-SSE) + the ADR-035 D8 pipeline:
 *   - ``GET /api/system/snapshot`` (:type:`SystemStatusSnapshot`) is the
 *     **full-detail** base — every component's rollup PLUS inline
 *     resource/domain/connection signals. Initial paint + reconnect re-fetch.
 *   - ``GET /api/stream/system-status`` (:type:`StatusStreamFrame`) is the
 *     **lightweight rollup summary** — stoplights only (per-component
 *     rollup_state + plane/overall). The rich detail is NOT on the stream.
 *
 * :func:`mergeStreamFrame` overlays the live stream rollups onto the snapshot
 * so stoplights animate live while inline signals track the last snapshot.
 *
 * # Why hand-rolled guards, not zod
 *
 * The SSE payload is a :class:`StreamingResponse` — it never surfaces in the
 * BFF ``openapi.yaml`` (the S5c codegen note), so there is no generated type.
 * ``zod`` is not a UI dependency (the tech-stack add is deferred per
 * CLAUDE.md §4.3); this module mirrors the established hand-rolled
 * trust-boundary guard pattern of :file:`@/map/sse-types` (CLAUDE.md §2.2 —
 * a runtime guard rejects any drifted payload so it fails 5xx-shaped rather
 * than rendering a false green, CLAUDE.md §8.1).
 *
 * Cross-references:
 *   - Flow L (docs/engineering/detail/storyboards/flows/L-system-status).
 *   - ADR-035 (System Status Telemetry) D6 (central rollup) / D8 (snapshot).
 *   - REQ-STATUS-016 (unknown never a false green); REQ-STATUS-020 (snapshot).
 *   - REQ-UI-046 (status palette — distinct + WCAG-AA across themes).
 *   - schemas/proto/meridian/v1/status/report.proto (the wire enums).
 */
import type { components } from "@/lib/api-types.gen";

// ---- Wire types (re-exported from the committed OpenAPI codegen) -----------

/** The full-detail snapshot (``GET /api/system/snapshot``; REQ-STATUS-020). */
export type SystemStatusSnapshot = components["schemas"]["SystemStatusSnapshotModel"];
/** One component instance's rollup + inline detail (snapshot ``components[]``). */
export type StatusComponent = components["schemas"]["ComponentModel"];
/** A plane's aggregate rollup (snapshot/stream ``planes[]``). */
export type PlaneRollup = components["schemas"]["PlaneRollupModel"];
/** Container-scoped resource utilization; any ``null`` field = "not collected". */
export type StatusResource = components["schemas"]["ResourceModel"];
/** The lazy per-connection list (``GET …/components/{id}/connections``; REQ-STATUS-021). */
export type ComponentConnections = components["schemas"]["ComponentConnectionsModel"];
/** One external connection's status (detail-pane row; Frame 3). */
export type StatusConnection = components["schemas"]["ConnectionModel"];

// ---- Rollup-state vocabulary (proto ComponentRollup.rollup_state) ----------

/**
 * The rollup states on the wire (report.proto ``ComponentRollup``; ADR-035 D6).
 * Lowercased enum tokens. Distinct from the retired System Health's six-state
 * taxonomy — the collector emits exactly these. ``standby`` joined the original
 * four with the ADR-036 §D-7 cascade; ``stopped`` joined with ADR-039 §D-4. Both
 * are **component** states only, never a plane or overall one (a plane of only
 * deliberately-idle components rolls up healthy).
 *
 * ⚠ ``standby`` and ``stopped`` are NOT interchangeable. ``standby`` says another
 * replica is carrying this flow; ``stopped`` says nothing is, deliberately. They
 * make opposite claims about whether data is moving, so collapsing them would
 * tell an operator a peer is covered when it is dark.
 */
export const ROLLUP_STATES = [
  "healthy",
  "standby",
  "stopped",
  "degraded",
  "unhealthy",
  "unknown",
] as const;
export type RollupState = (typeof ROLLUP_STATES)[number];

/**
 * Coerce an arbitrary wire string to a :type:`RollupState`, **failing safe to
 * ``unknown``** (REQ-STATUS-016 / CLAUDE.md §8.1 — an unrecognized token is
 * never rendered as a false green). The server owns the rollup; this guards
 * only against drift / a malformed frame.
 */
export function parseRollupState(value: string): RollupState {
  return (ROLLUP_STATES as readonly string[]).includes(value) ? (value as RollupState) : "unknown";
}

/** The two planes on the wire (report.proto ``Plane``; ``.name.lower()``). */
export type PlaneToken = "data" | "control";

/**
 * The functional component classification on the wire (report.proto
 * ``ComponentKind``; ``.name.lower()``, prefix stripped). Drives the
 * network-parametric node composition in :file:`./status-topology`.
 *
 * Hand-maintained, not generated: the proto enum reaches the UI only as an
 * opaque string on the OpenAPI models, so :func:`parseComponentKind` is the
 * boundary that decides which kinds this build knows how to draw. A kind the
 * list omits is dropped rather than guessed — forward-compatible, but it also
 * means **adding a kind to report.proto is not enough to make it appear**; it
 * must be added here too (the ADR-036 §D-1 egress kinds were exactly that case).
 *
 * ``peer_cds`` was removed on 2026-08-11 (generalized-peers §2.9.1, S-7). It named
 * the Peer Manager SERVICE's own report under a CDS-shaped kind, while the CDS
 * peer has reported as ``peer`` (``peer:cds``) since ADR-039 §D-4; the service now
 * reports as ``peer_manager``. The general Peer Interfaces (ADR-036) still report
 * per *interface* under ``peer_sapient`` / ``peer_raw``.
 */
export type ComponentKind =
  | "time_series"
  | "bff"
  | "peer_sapient"
  | "peer_raw"
  | "peer"
  | "peer_manager"
  | "provider_manager"
  | "provider"
  | "message_bus"
  | "data_store"
  | "raw_data_store"
  | "ui"
  | "collector";

// ---- Display metadata (REQ-UI-046 status palette) --------------------------

/**
 * A rollup state's display metadata (REQ-UI-046 — paired label + icon + token;
 * state never by colour alone, WCAG-AA ≥3:1 in both themes).
 *
 * - ``label`` — the screen-reader + visible text label.
 * - ``dotColor`` — the raw-hex ``--status-*`` CSS var (SVG ``fill``-ready; the
 *   MapLibre-facing literal set, resolvable in SVG DOM).
 * - ``tintFill`` — the node background fill. Non-healthy states carry a status
 *   tint; healthy / unknown stay a neutral surface (matches the storyboard mock
 *   — only non-healthy nodes are tinted, §3 "Non-healthy nodes get a tinted
 *   background").
 * - ``textClass`` — the Tailwind text-colour utility for the state word.
 * - ``glyph`` — the distinct icon key (paired with colour per REQ-UI-046).
 */
export interface RollupStateMeta {
  label: string;
  dotColor: string;
  tintFill: string;
  textClass: string;
  glyph: "check" | "warning" | "cross" | "question" | "pause" | "stop";
}

const NEUTRAL_TINT = "hsl(var(--meridian-surface-2))";

const ROLLUP_STATE_META: Record<RollupState, RollupStateMeta> = {
  healthy: {
    label: "Healthy",
    dotColor: "var(--status-healthy)",
    tintFill: NEUTRAL_TINT,
    textClass: "text-meridian-status-healthy",
    glyph: "check",
  },
  degraded: {
    label: "Degraded",
    dotColor: "var(--status-degraded)",
    tintFill: "hsl(var(--meridian-tint-degraded))",
    textClass: "text-meridian-status-degraded",
    glyph: "warning",
  },
  unhealthy: {
    label: "Unhealthy",
    dotColor: "var(--status-unhealthy)",
    tintFill: "hsl(var(--meridian-tint-unhealthy))",
    textClass: "text-meridian-status-unhealthy",
    glyph: "cross",
  },
  unknown: {
    label: "Unknown",
    dotColor: "var(--status-unknown)",
    tintFill: NEUTRAL_TINT,
    textClass: "text-meridian-status-unknown",
    glyph: "question",
  },
  /**
   * Deliberately not working — an operator-stopped Peer Interface, or an
   * active-passive replica that does not hold its lease (ADR-036 §D-7 cascade;
   * ADR-020). Grey per Flow L §8 ("grey — unknown / **intentionally standby**"),
   * and NOT tinted: §3 tints only non-healthy nodes to draw the eye, and a
   * deliberately-silenced flow is not something to draw the eye to.
   *
   * Uses ``--status-stale`` rather than ``--status-unknown`` so standby and
   * unknown stay visually separable — they mean opposite things ("we know, and
   * it is off" vs "we do not know"), and the ``pause`` glyph carries the
   * distinction where colour cannot (REQ-UI-046: never by colour alone).
   */
  standby: {
    label: "Standby",
    dotColor: "var(--status-stale)",
    tintFill: NEUTRAL_TINT,
    textClass: "text-meridian-status-stale",
    glyph: "pause",
  },
  /**
   * Deliberately not working, and **nothing else is carrying it** — an
   * operator-stopped peer, interface or connection (ADR-039 §D-4 R1). The
   * opposite claim from ``standby``, which asserts another replica has the flow.
   *
   * Shares ``standby``'s grey, because neither is a fault and neither should
   * draw the eye the way an amber or red does — so the ``stop`` glyph carries
   * the whole distinction (REQ-UI-046: never by colour alone). Grey rather than
   * a new hue is deliberate: adding a sixth colour to the status palette would
   * cost contrast budget on a surface that already spends it on the three that
   * signal a fault.
   *
   * Not tinted, for the same reason ``standby`` is not: §3 tints only non-healthy
   * nodes to draw attention, and a deliberately-silenced flow is not something to
   * draw attention to.
   */
  stopped: {
    label: "Stopped",
    dotColor: "var(--status-stale)",
    tintFill: NEUTRAL_TINT,
    textClass: "text-meridian-status-stale",
    glyph: "stop",
  },
};

/** Resolve a rollup state's display metadata (REQ-UI-046). */
export function rollupStateMeta(state: RollupState): RollupStateMeta {
  return ROLLUP_STATE_META[state];
}

/**
 * Display priority for picking the single stoplight a node shows when its
 * component kind has multiple instances (unhealthy → degraded → unknown →
 * healthy). Surfaces the most actionable state while **never showing green if
 * any instance is non-green** (REQ-STATUS-016 never-false-green). This is a UI
 * display choice; the authoritative plane / overall rollup is the server's
 * (carried in the snapshot / stream unchanged).
 */
const DISPLAY_PRIORITY: Record<RollupState, number> = {
  unhealthy: 4,
  degraded: 3,
  unknown: 2,
  healthy: 1,
  // Lowest, so a kind with an ACTIVE and a STANDBY instance shows the active
  // one's state: the flow is being served, and that is what the node reports.
  // A kind whose every instance is standby still shows standby, because there
  // is then nothing higher to win. This does not weaken never-false-green —
  // that rule exists so a BROKEN instance is never masked, and standby is not
  // broken.
  standby: 0,
  // Same rank as `standby`, and for the same reason: neither is broken, so neither
  // should mask an instance that IS. A kind whose every instance is stopped still
  // shows stopped, because nothing higher wins — which is what keeps a peer that
  // is entirely dark from reading green (ADR-039 R3, at the display layer).
  stopped: 0,
};

/**
 * Reduce a set of rollup states to the one a node displays — the highest
 * display-priority (see :data:`DISPLAY_PRIORITY`). An empty set → ``unknown``
 * (a node whose kind the snapshot doesn't report is honestly unknown, never a
 * false green — REQ-STATUS-016 / storyboard §11 "Component UNKNOWN").
 */
export function worstRollupState(states: readonly RollupState[]): RollupState {
  if (states.length === 0) return "unknown";
  return states.reduce((worst, s) => (DISPLAY_PRIORITY[s] > DISPLAY_PRIORITY[worst] ? s : worst));
}

// ---- Hand-authored SSE-frame types + runtime guard -------------------------

/**
 * One component's rollup on the SSE frame (``event: system-status``). The
 * lightweight stoplight summary — a subset of :type:`StatusComponent` (no
 * resource / domain / connection detail; that comes from the REST snapshot).
 * Matches the BFF ``ComponentRollupModel`` (S5b) verbatim.
 */
export interface StreamComponentRollup {
  component_id: string;
  component_kind: string;
  plane: string;
  instance_id: string;
  rollup_state: string;
  rollup_severity: number;
  rollup_reason: string;
  generated_at: string;
}

/**
 * The SSE frame the BFF fans out on ``GET /api/stream/system-status`` (S5b).
 * Hand-authored (the StreamingResponse has no OpenAPI body schema — the S5c
 * codegen note). Shape = the proto ``SystemStatusSnapshot`` rollup summary,
 * snake_case, lowercased enum tokens.
 */
export interface StatusStreamFrame {
  network: string;
  generated_at: string;
  overall_state: string;
  overall_severity: number;
  overall_unknown_count: number;
  planes: PlaneRollup[];
  components: StreamComponentRollup[];
}

function isPlaneRollup(value: unknown): value is PlaneRollup {
  if (typeof value !== "object" || value === null) return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.plane === "string" &&
    typeof o.rollup_state === "string" &&
    typeof o.rollup_severity === "number" &&
    typeof o.unknown_count === "number"
  );
}

function isStreamComponentRollup(value: unknown): value is StreamComponentRollup {
  if (typeof value !== "object" || value === null) return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.component_id === "string" &&
    typeof o.component_kind === "string" &&
    typeof o.plane === "string" &&
    typeof o.instance_id === "string" &&
    typeof o.rollup_state === "string" &&
    typeof o.rollup_severity === "number" &&
    typeof o.rollup_reason === "string" &&
    typeof o.generated_at === "string"
  );
}

/**
 * Parse + validate a raw SSE ``data:`` payload into a :type:`StatusStreamFrame`,
 * or ``null`` if it is malformed / drifted (trust-boundary guard; CLAUDE.md
 * §2.2 / §8.1 — a single bad frame is dropped, never rendered). Accepts the
 * already-JSON-parsed value or a raw string.
 */
export function parseStatusStreamFrame(raw: unknown): StatusStreamFrame | null {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof value !== "object" || value === null) return null;
  const o = value as Record<string, unknown>;
  if (
    typeof o.network !== "string" ||
    typeof o.generated_at !== "string" ||
    typeof o.overall_state !== "string" ||
    typeof o.overall_severity !== "number" ||
    typeof o.overall_unknown_count !== "number" ||
    !Array.isArray(o.planes) ||
    !Array.isArray(o.components)
  ) {
    return null;
  }
  if (!o.planes.every(isPlaneRollup)) return null;
  if (!o.components.every(isStreamComponentRollup)) return null;
  return {
    network: o.network,
    generated_at: o.generated_at,
    overall_state: o.overall_state,
    overall_severity: o.overall_severity,
    overall_unknown_count: o.overall_unknown_count,
    planes: o.planes,
    components: o.components,
  };
}

// ---- Snapshot ⊕ stream merge -----------------------------------------------

/** Identity key for a component instance (``component_id`` + ``instance_id``). */
export function componentKey(componentId: string, instanceId: string): string {
  return `${componentId}::${instanceId}`;
}

/**
 * A component after overlaying the live stream rollup onto the snapshot detail.
 *
 * ``rollup_state`` / ``rollup_severity`` / ``rollup_reason`` track the live SSE
 * frame when present. The inline signals (resource / domain / connection
 * counts) and ``generated_at`` track the REST snapshot, **which is the point**:
 * ``generated_at`` is the age of the numbers in this record, and the display
 * renders it as that record's "as of" stamp.
 *
 * Until 2026-10-04 the merge overwrote ``generated_at`` from the live frame,
 * so every Selection Detail pane printed a stamp seconds old above values that
 * could be minutes old — asserting a freshness the numbers did not have. The
 * stamp now understates the stoplight's freshness rather than overstating the
 * detail's, which is the safe direction (CLAUDE.md §8.1; the REQ-STATUS-016
 * "never a false green" reasoning, applied to values rather than colours). With
 * REQ-STATUS-099's live-frame refresh the two converge to within one cadence in
 * the healthy case, so this matters precisely when something has gone wrong.
 *
 * Structurally identical to :type:`StatusComponent`; the distinct name is kept
 * deliberately, because it marks a component that has been through
 * :func:`mergeStreamFrame` and that provenance is what every consumer of this
 * type relies on. It carried a ``live: boolean`` discriminator from PR #675
 * until 2026-10-04, and nothing ever read it. The divergence it flagged — a
 * REST snapshot naming a component the live frame no longer does — is
 * prevented server-side by REQ-STATUS-034's shared staleness horizon and
 * closed client-side by REQ-STATUS-099's live-frame refresh above, while "this
 * browser's feed is not live" is already operator-visible through the
 * status-stream row and REQ-STATUS-048's top-bar ``Stale`` badge. Retired per
 * the design-ahead precedent in the system specification §5: a hook whose
 * consumer never arrived is retired when its successor lands, not carried.
 */
export type ResolvedComponent = StatusComponent;

/**
 * Overlay a live :type:`StatusStreamFrame`'s per-component rollups onto the
 * snapshot's components, keyed by (component_id, instance_id). A component
 * present only in the snapshot keeps its snapshot rollup; a component whose
 * rollup the stream updates gets the fresh stoplight while retaining the
 * snapshot's rich inline detail (the
 * stream carries no resource / domain / connections — storyboard §6). Passing
 * ``frame = null`` (pre-first-frame / SSE down) returns the snapshot
 * components unchanged.
 */
export function mergeStreamFrame(
  snapshot: SystemStatusSnapshot,
  frame: StatusStreamFrame | null,
): ResolvedComponent[] {
  if (frame === null) {
    return snapshot.components.map((c) => ({ ...c }));
  }
  const liveByKey = new Map<string, StreamComponentRollup>();
  for (const c of frame.components) {
    liveByKey.set(componentKey(c.component_id, c.instance_id), c);
  }
  return snapshot.components.map((c) => {
    const live = liveByKey.get(componentKey(c.component_id, c.instance_id));
    if (live === undefined) return { ...c };
    return {
      ...c,
      rollup_state: live.rollup_state,
      rollup_severity: live.rollup_severity,
      rollup_reason: live.rollup_reason,
      // `generated_at` is deliberately NOT taken from the frame — it stamps the
      // detail this record carries, which is the snapshot's. See the
      // `ResolvedComponent` docstring.
    };
  });
}

// ---- Node resolution (snapshot components → diagram node signals) ----------

const COMPONENT_KINDS: readonly ComponentKind[] = [
  "time_series",
  "bff",
  "peer_sapient",
  "peer_raw",
  "peer",
  "peer_manager",
  "provider_manager",
  "provider",
  "message_bus",
  "data_store",
  "raw_data_store",
  "ui",
  "collector",
];

/** Coerce a wire ``component_kind`` string to a known :type:`ComponentKind`, or null. */
export function parseComponentKind(value: string): ComponentKind | null {
  return (COMPONENT_KINDS as readonly string[]).includes(value) ? (value as ComponentKind) : null;
}

/**
 * The set of component kinds the snapshot reports — the input to the
 * network-parametric composition (:func:`composeNodes`). Unknown wire kinds are
 * dropped (forward-compatible; a future kind simply isn't a node yet).
 */
export function reportedKinds(components: readonly StatusComponent[]): Set<ComponentKind> {
  const kinds = new Set<ComponentKind>();
  for (const c of components) {
    const kind = parseComponentKind(c.component_kind);
    if (kind !== null) kinds.add(kind);
  }
  return kinds;
}

/** Group components by their (parsed) kind — one node may map to several instances. */
export function groupByKind(
  components: readonly ResolvedComponent[],
): Map<ComponentKind, ResolvedComponent[]> {
  const byKind = new Map<ComponentKind, ResolvedComponent[]>();
  for (const c of components) {
    const kind = parseComponentKind(c.component_kind);
    if (kind === null) continue;
    const list = byKind.get(kind);
    if (list === undefined) byKind.set(kind, [c]);
    else list.push(c);
  }
  return byKind;
}

/**
 * The single stoplight a node shows for its kind — worst-of the instances'
 * rollups (:func:`worstRollupState`), or ``unknown`` when the snapshot reports
 * no instance of that kind (honest grey; storyboard §11, REQ-STATUS-016).
 */
export function nodeRollupState(components: readonly ResolvedComponent[] | undefined): RollupState {
  if (components === undefined || components.length === 0) return "unknown";
  return worstRollupState(components.map((c) => parseRollupState(c.rollup_state)));
}

/** Round a fraction/percentage to a compact integer string. */
function pct(value: number): string {
  return `${Math.round(value)}%`;
}

/**
 * The 1-line inline signal a service/store node shows under its name
 * (storyboard §3 "1-2 inline summary signals"). A non-healthy node surfaces
 * the server's rollup **reason** (the driving input, e.g. ``disk_pct=91``); a
 * healthy node surfaces a light resource hint (cpu / mem) when collected. Null
 * when nothing is available (the node shows its name + stoplight alone).
 *
 * v1 keeps the signal honest + field-driven; friendlier per-kind domain
 * metrics (msgs/s, disk %) land with the detail pane (§ follow-up increment).
 */
/**
 * Which denominator a resource percentage was computed against, or ``null``
 * when there is no percentage to describe.
 *
 * The wire carries no explicit basis field: the ``(percent, limit)`` pair
 * determines it, because the limit is never fabricated (REQ-STATUS-006 / -008
 * amended 2026-08-01; ADR-035 Amendment — 2026-08-01). A percent with a limit
 * is limit-relative; a percent **without** one was divided by the host basis
 * (available cores / ``MemTotal``) because the container sets no cgroup limit —
 * which is the norm, since no Meridian Compose topology sets one.
 *
 * ``"host"`` is the honest-but-weaker reading: a service saturating one core of
 * a 64-core host shows ~1.6%, so the number describes host headroom rather than
 * per-service saturation. Callers must surface the distinction rather than
 * presenting a bare percentage.
 */
export function resourcePercentBasis(
  percent: number | null | undefined,
  limit: number | null | undefined,
): "limit" | "host" | null {
  if (percent === null || percent === undefined) return null;
  return limit === null || limit === undefined ? "host" : "limit";
}

export function componentInlineSignal(component: ResolvedComponent): string | null {
  const state = parseRollupState(component.rollup_state);
  if (state !== "healthy" && component.rollup_reason.trim() !== "") {
    const reason = component.rollup_reason.trim();
    return reason.length > 32 ? `${reason.slice(0, 31)}…` : reason;
  }
  const { cpu_pct, mem_pct } = component.resource;
  if (cpu_pct !== null && cpu_pct !== undefined) return `cpu ${pct(cpu_pct)}`;
  if (mem_pct !== null && mem_pct !== undefined) return `mem ${pct(mem_pct)}`;
  return null;
}
